<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-12" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = ''">
            {{ error }}
          </v-alert>

          <div class="cli-toolbar">
            <h2 class="cli-heading">ADMINISTRACIÓN / CLIENTES</h2>
            <button type="button" class="cli-new-btn" @click="goToNew">
              CREAR NUEVO CLIENTE
            </button>
          </div>

          <div class="cli-panel">
            <div class="cli-panel-header">
              <v-icon size="22" color="#ffffff">mdi-account-group-outline</v-icon>
              <span>CLIENTES</span>
            </div>

            <div class="cli-panel-body">
              <div class="cli-filters">
              <div class="cli-search">
                <v-icon size="18" color="#9a9a9a">mdi-magnify</v-icon>
                <input
                  v-model="search"
                  type="search"
                  class="cli-search-input"
                  placeholder="Buscar por cliente, contacto o correo"
                />
              </div>

              <select v-model="companyType" class="cli-type-select" aria-label="Tipo de empresa">
                <option value="">Todos los tipos de empresa</option>
                <option v-for="t in companyTypeOptions" :key="t" :value="t">
                  {{ t }}
                </option>
              </select>

              <label class="cli-inactive-toggle">
                <input v-model="showInactive" type="checkbox" />
                Mostrar inactivos
              </label>
              </div>

              <div v-if="loading" class="d-flex justify-center py-8">
                <v-progress-circular indeterminate color="primary" size="40" />
              </div>

              <div v-else class="cli-table-scroll">
                <table class="cli-table">
                  <thead>
                    <tr>
                      <th>CLIENTE</th>
                      <th>CONTACTO</th>
                      <th>TELÉFONO</th>
                      <th>CORREO</th>
                      <th class="cli-col-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="c in clients" :key="c.id" :class="{ 'cli-row-off': !c.is_active }">
                      <td class="cli-cell-name">
                        {{ c.company_name || '—' }}
                        <span v-if="c.client_type_display" class="cli-tag">
                          {{ c.client_type_display }}
                        </span>
                        <span v-if="c.company_type" class="cli-tag cli-tag-type">
                          {{ c.company_type }}
                        </span>
                        <span v-if="!c.is_active" class="cli-tag cli-tag-off">INACTIVO</span>
                      </td>
                      <td>{{ c.full_name || '—' }}</td>
                      <td>{{ c.phone || '—' }}</td>
                      <td>{{ c.email }}</td>
                      <td>
                        <button type="button" class="cli-row-btn" @click="goToEdit(c)">
                          VER / EDITAR
                        </button>
                        <button
                          type="button"
                          class="cli-row-btn cli-row-btn-danger"
                          :disabled="deletingId === c.id"
                          @click="remove(c)"
                        >
                          {{ deletingId === c.id ? 'ELIMINANDO…' : 'ELIMINAR' }}
                        </button>
                      </td>
                    </tr>
                    <tr v-if="clients.length === 0">
                      <td colspan="5" class="cli-empty">
                        {{ search || companyType ? 'Ningún cliente coincide con los filtros.' : showInactive ? 'Todavía no hay clientes registrados.' : 'No hay clientes activos.' }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <teatro-footer />
  </div>
</template>

<script>
import { fetchClients, fetchClientFilterOptions, deleteClient } from "@/services/teatroApi";

export default {
  name: "ClientList",
  data() {
    return {
      clients: [],
      search: "",
      companyType: "",
      showInactive: false,
      companyTypeOptions: [],
      searchTimer: null,
      loading: true,
      error: "",
      deletingId: null,
    };
  },
  watch: {
    // La búsqueda va al backend; se espera a que el usuario deje de escribir.
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.load(), 300);
    },
    // El tipo de empresa también filtra en el backend.
    companyType() {
      clearTimeout(this.searchTimer);
      this.load();
    },
    // Los inactivos se piden al backend sólo con la casilla marcada.
    showInactive() {
      clearTimeout(this.searchTimer);
      this.load();
    },
  },
  async created() {
    await this.loadAuth();
    await Promise.all([this.load(), this.loadFilterOptions()]);
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    async loadAuth() {
      try {
        await this.$store.dispatch("fetchIsAuthenticated");
      } catch (e) {
        console.error(e);
      }
    },
    async load() {
      this.error = "";
      try {
        this.clients =
          (await fetchClients(this.search, "", this.companyType, this.showInactive)) || [];
      } catch (e) {
        this.error = "No se pudieron cargar los clientes.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async loadFilterOptions() {
      try {
        const options = await fetchClientFilterOptions();
        this.companyTypeOptions = options.company_types || [];
      } catch (e) {
        // Sin opciones el listado sigue funcionando, sólo sin filtros.
        console.error("No se pudieron cargar los filtros de clientes", e);
      }
    },
    goToNew() {
      window.location.href = "/clientes/nuevo/";
    },
    // Borrado definitivo de la cuenta; las reservas que hizo quedan.
    async remove(c) {
      const who = [c.company_name, c.full_name || c.email].filter(Boolean).join(" · ");
      const ok = window.confirm(
        `¿Eliminar al cliente ${who}?\n\nSe borra su cuenta de forma definitiva. ` +
          "Sus reservas se conservan con el nombre y correo registrados."
      );
      if (!ok) return;
      this.deletingId = c.id;
      this.error = "";
      try {
        await deleteClient(c.id);
        this.clients = this.clients.filter((x) => x.id !== c.id);
      } catch (e) {
        const data = e.response && e.response.data;
        this.error = (data && data.detail) || "No se pudo eliminar el cliente.";
        console.error(e);
      } finally {
        this.deletingId = null;
      }
    },
    goToEdit(c) {
      window.location.href = `/clientes/${c.id}/editar/`;
    },
  },
};
</script>

<style scoped>
.cli-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  max-width: 1080px;
  margin: 0 auto 22px;
  flex-wrap: wrap;
}

.cli-heading {
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 1.2px;
  margin: 0;
}

.cli-new-btn {
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.cli-new-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(200, 16, 46, 0.4);
}

.cli-panel {
  max-width: 1080px;
  margin: 0 auto;
  background: rgba(8, 5, 5, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}

.cli-panel-header {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 66px;
  padding: 0 32px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1.4px;
}

.cli-panel-body {
  padding: 24px 34px 30px;
}

.cli-filters {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 22px;
}

.cli-search {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 260px;
  max-width: 420px;
  padding: 9px 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 999px;
}

.cli-search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 12px;
  letter-spacing: 0.4px;
}

.cli-search-input::placeholder {
  color: rgba(255, 255, 255, 0.45);
}

.cli-inactive-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 12px;
  letter-spacing: 0.4px;
  cursor: pointer;
  user-select: none;
}
.cli-inactive-toggle input {
  width: 16px;
  height: 16px;
  accent-color: #c8102e;
  cursor: pointer;
}
.cli-type-select {
  max-width: 100%;
  padding: 9px 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 999px;
  outline: none;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 12px;
  letter-spacing: 0.4px;
  cursor: pointer;
}

/* La lista desplegable nativa usa fondo claro en algunos navegadores. */
.cli-type-select option {
  background: #1a1414;
  color: #fff;
}

.cli-table-scroll {
  overflow-x: auto;
}

.cli-table {
  width: 100%;
  border-collapse: collapse;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
}

.cli-table th {
  text-align: left;
  padding: 8px 16px 18px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
}

.cli-table td {
  padding: 15px 16px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  vertical-align: middle;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.cli-table tbody tr:first-child td {
  border-top: none;
}

.cli-cell-name {
  text-transform: uppercase;
}

.cli-row-off {
  opacity: 0.55;
}

.cli-tag {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.75);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.6px;
}

.cli-tag-type {
  text-transform: none;
}

.cli-tag-off {
  background: rgba(200, 16, 46, 0.25);
  color: #ff8ea0;
}

.cli-col-actions {
  width: 1%;
}

.cli-row-btn {
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 9px 18px;
  font-family: var(--font-family-heading);
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.8px;
  white-space: nowrap;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.cli-row-btn-danger {
  margin-left: 6px;
  background: transparent;
  color: #ff8a95;
  border: 1px solid rgba(200, 16, 46, 0.6);
}

.cli-row-btn-danger:hover:not(:disabled) {
  background: rgba(200, 16, 46, 0.18);
}

.cli-row-btn-danger:disabled {
  opacity: 0.6;
  cursor: default;
}

.cli-row-btn:hover {
  transform: translateY(-1px);
}

.cli-empty {
  text-align: center;
  padding: 30px 16px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
}

@media (max-width: 700px) {
  .cli-toolbar {
    justify-content: center;
    text-align: center;
  }
}
</style>
